import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

type DocumentItem = {
  filename: string;
  company: string;
  chunk_count: number;
  uploaded_at: string;
};

type CollectionItem = {
  company_name: string;
  file_count: number;
  document_count: number;
  collection_name: string;
};

type MessageSource = {
  filename?: string;
  similarity?: number;
};

type ChatMessage = {
  type: "user" | "assistant";
  text: string;
  sources?: MessageSource[];
  mode?: string;
  timestamp?: Date;
};

const API_BASE_URL = `${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/api`;

const DocumentChat = () => {
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [collections, setCollections] = useState<CollectionItem[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [uploadCompany, setUploadCompany] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    Promise.all([fetchDocuments(), fetchCompanies(), fetchCollections()]).catch((error) => {
      console.error("Error during initial data fetch:", error);
    });
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const startPageLoading = () => setPendingRequests((prev) => prev + 1);
  const endPageLoading = () => setPendingRequests((prev) => Math.max(0, prev - 1));

  const fetchDocuments = async () => {
    startPageLoading();
    try {
      const response = await axios.get(`${API_BASE_URL}/documents`);
      const list = Array.isArray(response.data?.documents) ? response.data.documents : [];
      setDocuments(list);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      endPageLoading();
    }
  };

  const fetchCompanies = async () => {
    startPageLoading();
    try {
      const response = await axios.get(`${API_BASE_URL}/rag/companies`);
      const list = Array.isArray(response.data?.companies) ? response.data.companies : [];
      setCompanies(list);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      endPageLoading();
    }
  };

  const fetchCollections = async () => {
    startPageLoading();
    try {
      const response = await axios.get(`${API_BASE_URL}/rag/collections`);
      const list = Array.isArray(response.data?.collections) ? response.data.collections : [];
      setCollections(list);
    } catch (error: any) {
      console.error("Error fetching collections:", error);
      console.error("Error details:", error?.response?.data);
    } finally {
      endPageLoading();
    }
  };

  const handleFileUpload = async (file: File | null) => {
    if (!file) return;

    if (!uploadCompany || uploadCompany.trim() === "") {
      alert("Please enter a company name before uploading.");
      return;
    }

    const validExtensions = ["pdf", "docx", "xlsx"];
    const fileExt = file.name.toLowerCase().split(".").pop() || "";

    if (!validExtensions.includes(fileExt)) {
      alert("Please upload PDF, Word (docx), or Excel (xlsx) files only.");
      return;
    }

    setIsUploading(true);
    startPageLoading();
    const formData = new FormData();
    formData.append("file", file);
    formData.append("company", uploadCompany.trim());

    try {
      const response = await axios.post(`${API_BASE_URL}/upload-document`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert(`Document uploaded successfully for ${uploadCompany}! ${response.data?.chunks_created || 0} chunks created.`);
      setUploadCompany("");
      await Promise.all([fetchDocuments(), fetchCompanies(), fetchCollections()]);
    } catch (error) {
      console.error("Error uploading document:", error);
      alert("Error uploading document. Please try again.");
    } finally {
      setIsUploading(false);
      endPageLoading();
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      handleFileUpload(file);
    }
    event.target.value = "";
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0] || null;
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDeleteDocument = async (filename: string, company: string) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}" from ${company}?`)) {
      return;
    }

    startPageLoading();
    try {
      await axios.delete(`${API_BASE_URL}/documents/${encodeURIComponent(filename)}?company=${encodeURIComponent(company)}`);
      alert("Document deleted successfully");
      await Promise.all([fetchDocuments(), fetchCompanies(), fetchCollections()]);
    } catch (error) {
      console.error("Error deleting document:", error);
      alert("Error deleting document. Please try again.");
    } finally {
      endPageLoading();
    }
  };

  const handleDeleteCollection = async (collectionName: string, companyName: string) => {
    if (!window.confirm(`Are you sure you want to DELETE the entire collection for "${companyName}"?\n\nThis will permanently delete ALL documents and data for this company.\n\nThis action cannot be undone!`)) {
      return;
    }

    startPageLoading();
    try {
      await axios.delete(`${API_BASE_URL}/rag/collections/${encodeURIComponent(collectionName)}`);
      alert(`Collection for ${companyName} deleted successfully`);
      await Promise.all([fetchDocuments(), fetchCompanies(), fetchCollections()]);
    } catch (error) {
      console.error("Error deleting collection:", error);
      alert("Error deleting collection. Please try again.");
    } finally {
      endPageLoading();
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");

    setMessages((prev) => [
      ...prev,
      { type: "user", text: userMessage, timestamp: new Date() },
    ]);

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/chat`, {
        query: userMessage,
        mode: "rag",
        top_k: 5,
        company: selectedCompany,
      });

      setMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          text: response.data?.answer || "No response available.",
          sources: Array.isArray(response.data?.sources) ? response.data.sources : [],
          mode: response.data?.mode,
          timestamp: new Date(),
        },
      ]);
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          text: "Sorry, I encountered an error processing your request. Please try again.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 relative">
      {pendingRequests > 0 && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[2000]">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-primary/30 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">Loading...</div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-950 rounded-xl shadow-lg p-4 mb-4">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">📄 Document Management</h2>
      </div>

      <div
        className={`bg-white dark:bg-gray-950 rounded-xl shadow-lg p-6 mb-6 border-2 ${isDragging ? "border-primary bg-primary/5" : "border-dashed border-gray-300 dark:border-gray-600"} transition-all duration-200`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="mb-3">
          <input
            type="text"
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Enter company name (e.g., Apple, Microsoft)"
            value={uploadCompany}
            onChange={(event) => setUploadCompany(event.target.value)}
          />
        </div>
        <input ref={fileInputRef} type="file" accept=".pdf,.docx,.xlsx" onChange={handleFileSelect} className="hidden" />
        <button
          className="w-full px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading || !uploadCompany.trim()}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          {isUploading ? "Uploading..." : "Upload Document"}
        </button>
        <div className="text-sm text-center text-gray-500 dark:text-gray-400 mt-2">Supported formats: PDF, Word (.docx), Excel (.xlsx) • Drag & drop or click to upload</div>
      </div>

      {documents.length > 0 && (
        <div className="bg-white dark:bg-gray-950 rounded-xl shadow-lg p-6 mb-6">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">📚 Uploaded Documents ({documents.length})</h4>
          <div className="space-y-3">
            {documents.map((doc, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">{doc.filename}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Company: <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">{doc.company}</span> • {doc.chunk_count} chunks • Uploaded {new Date(doc.uploaded_at).toLocaleDateString()}
                  </div>
                </div>
                <button className="ml-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium text-sm" onClick={() => handleDeleteDocument(doc.filename, doc.company)}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {collections.length > 0 && (
        <div className="bg-white dark:bg-gray-950 rounded-xl shadow-lg p-6 mb-6">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">🗂️ Company Collections ({collections.length})</h4>
          <div className="space-y-3">
            {collections.map((collection, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/20 dark:to-primary/10 rounded-lg hover:from-primary/10 hover:to-primary/15 dark:hover:from-primary/30 dark:hover:to-primary/20 transition-all border border-primary/20">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 dark:text-white text-lg">{collection.company_name}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1">{collection.file_count} {collection.file_count === 1 ? "file" : "files"}</span>
                    <span>•</span>
                    <span className="inline-flex items-center gap-1">{collection.document_count} chunks</span>
                    <span>•</span>
                    <span className="text-xs font-mono bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">{collection.collection_name}</span>
                  </div>
                </div>
                <button
                  className="ml-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium text-sm flex items-center gap-2 shadow-md hover:shadow-lg"
                  onClick={() => handleDeleteCollection(collection.collection_name, collection.company_name)}
                  title="Delete entire collection"
                >
                  Delete Collection
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isChatOpen && (
        <div className="fixed bottom-28 right-8 w-[420px] max-h-[550px] bg-white dark:bg-gray-950 rounded-xl shadow-2xl flex flex-col z-[1000] overflow-hidden animate-slideUp">
          <div className="bg-primary text-white px-5 py-4 rounded-t-xl flex justify-between items-center">
            <h3 className="text-lg font-semibold">🤖 Explore KPIs</h3>
            <button className="text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition-colors font-bold text-xl" onClick={() => setIsChatOpen(false)}>✕</button>
          </div>

          <div className="px-5 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <label htmlFor="company-select" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">Search in:</label>
            <select id="company-select" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-600 text-gray-900 dark:text-white text-sm" value={selectedCompany} onChange={(event) => setSelectedCompany(event.target.value)}>
              <option value="all">All Companies</option>
              {companies.map((company, index) => (
                <option key={index} value={company}>{company}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50 dark:bg-gray-950">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <div className="text-6xl mb-4">💬</div>
                <div className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Start a conversation!</div>
                <div className="text-sm text-gray-600 dark:text-gray-400 max-w-xs">Upload documents and ask questions about KPIs, benchmarks, or any data in your files.</div>
              </div>
            ) : (
              <>
                {messages.map((message, index) => (
                  <div key={index} className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-[18px] ${message.type === "user" ? "bg-primary text-white rounded-br-[4px] ml-auto" : "bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded-bl-[4px] shadow-md"}`}>
                      <div className="text-sm whitespace-pre-wrap break-words">{message.text}</div>
                      {Array.isArray(message.sources) && message.sources.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">📌 Sources:</div>
                          {message.sources.slice(0, 3).map((source, idx) => (
                            <div key={idx} className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded mb-1">
                              {source.filename} (similarity: {(((source.similarity || 0) as number) * 100).toFixed(1)}%)
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] px-4 py-3 rounded-[18px] bg-gray-100 dark:bg-gray-900 shadow-md rounded-bl-[4px]">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0s" }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.4s" }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          <div className="p-4 bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-600 flex gap-2">
            <input
              type="text"
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-full text-sm outline-none focus:border-primary dark:focus:border-primary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Ask a question about your uploaded documents..."
              value={inputMessage}
              onChange={(event) => setInputMessage(event.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
            <button className="px-4 py-2.5 bg-primary text-white rounded-full hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2 font-medium text-sm" onClick={handleSendMessage} disabled={isLoading || !inputMessage.trim()}>
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentChat;